import { DollarSign, ShoppingBag, MousePointerClick, Eye, Mail, Globe, XCircle, Download, FileText, TrendingUp, Clock, RefreshCw, AlertTriangle } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/25 rounded-sm p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-[0.2em] text-[#A1B0C6]">{label}</span>
      <Icon className="w-5 h-5 text-[#3F8EFC]" strokeWidth={1.5} />
    </div>
    <div className="font-display text-3xl sm:text-4xl font-black text-white">{value}</div>
    {sub && <div className="text-xs text-[#A1B0C6] mt-2">{sub}</div>}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const IPOPanel = ({ stats, onReconcile, reconciling, reconcileResult }) => {
  const s = stats || {};
  return (
    <div className="max-w-7xl mx-auto space-y-8" dir="ltr" data-testid="ipo-panel">
      {/* Reconcile / decline-codes strip */}
      <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/30 rounded-sm p-5 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between" data-testid="ipo-reconcile-bar">
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.22em] text-[#3F8EFC] font-bold mb-1">PAYMENT RECONCILIATION</div>
          <div className="text-[13px] text-[#A1B0C6] leading-relaxed">
            Asks Stripe directly about every unpaid order — recovers anything where the webhook never arrived and surfaces decline reasons for the rest.
          </div>
          {reconcileResult && (
            <div className="mt-2 text-[12.5px] text-[#5EEAD4]" data-testid="ipo-reconcile-result">
              Checked <span className="font-bold">{reconcileResult.summary?.checked || 0}</span> · Fixed <span className="font-bold text-white">{reconcileResult.summary?.fixed || 0}</span> · Failed <span className="font-bold">{reconcileResult.summary?.failed || 0}</span> · Expired <span className="font-bold">{reconcileResult.summary?.expired || 0}</span> · Still open <span className="font-bold">{reconcileResult.summary?.still_open || 0}</span>
            </div>
          )}
        </div>
        <button
          onClick={onReconcile}
          disabled={reconciling}
          data-testid="ipo-reconcile-btn"
          className="bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[13px] tracking-wide px-5 py-3 rounded-sm transition-all flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(63,142,252,0.6)] shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${reconciling ? "animate-spin" : ""}`} />
          {reconciling ? "Revising with Stripe…" : "Revise payments with Stripe"}
        </button>
      </div>

      {reconcileResult?.decline_codes?.length > 0 && (
        <div className="bg-[#0B1D3A]/70 border border-[#F87171]/25 rounded-sm p-5" data-testid="ipo-decline-codes">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#FCA5A5]" />
            <h3 className="text-[13px] font-bold tracking-wider text-white">DECLINE CODES (last reconcile)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {reconcileResult.decline_codes.map(([code, count]) => (
              <span key={code} className="text-[12px] bg-[#F87171]/[0.1] border border-[#F87171]/30 text-[#FCA5A5] px-2.5 py-1 rounded-sm">
                <span className="font-mono">{code}</span> <span className="font-bold text-white">×{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Revenue / orders KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={DollarSign} label="REVENUE" value={`$${(s.revenue_usd || 0).toFixed(2)}`} sub={`${s.orders_paid || 0} paid orders · $${(s.price_usd || 79).toFixed(2)} each`} testid="ipo-stat-revenue" />
        <Kpi icon={ShoppingBag} label="ORDERS" value={`${s.orders_paid || 0} / ${s.orders_total || 0}`} sub={`${s.checkout_conversion_pct || 0}% checkout conversion`} testid="ipo-stat-orders" />
        <Kpi icon={Download} label="DOWNLOADS" value={s.downloads_total || 0} sub={`${s.orders_paid ? Math.round((s.downloads_total || 0) / s.orders_paid * 100) : 0}% of buyers downloaded`} testid="ipo-stat-downloads" />
        <Kpi icon={TrendingUp} label="VISIT → PURCHASE" value={`${s.purchase_conversion_pct || 0}%`} sub={`${s.orders_paid || 0} paid / ${s.visits || 0} visits`} testid="ipo-stat-conversion" />
      </div>

      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={Eye} label="PAGE VISITS" value={s.visits || 0} sub="landing page views" testid="ipo-stat-visits" />
        <Kpi icon={MousePointerClick} label="CTA CLICKS" value={s.cta_clicks || 0} sub={`${s.ctr_pct || 0}% of visits`} testid="ipo-stat-cta" />
        <Kpi icon={Mail} label="EMAIL SUBMITS" value={s.email_submits || 0} sub={`${s.email_conversion_pct || 0}% of visits`} testid="ipo-stat-emails" />
        <Kpi icon={Clock} label="AVG TIME ON SITE" value={`${Math.floor((s.avg_time_on_site_sec || 0) / 60)}m ${(s.avg_time_on_site_sec || 0) % 60}s`} sub="per landing visit" testid="ipo-stat-tos" />
      </div>

      {/* Countries */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/25 rounded-sm p-6" data-testid="ipo-payment-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#3F8EFC]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS BY COUNTRY</h2>
          </div>
          {(s.payment_countries || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No payments yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.payment_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country || "Unknown"}</span>
                  <span className="text-[#A1B0C6]">{c.count} payment{c.count > 1 ? "s" : ""} · <span className="text-[#3F8EFC] font-bold">${(c.revenue_usd || 0).toFixed(2)}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/25 rounded-sm p-6" data-testid="ipo-visit-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#5EEAD4]" />
            <h2 className="text-sm font-bold tracking-wider text-white">VISITS BY COUNTRY</h2>
          </div>
          {(s.visit_countries || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No tracked visits yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.visit_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country}</span>
                  <span className="text-[#A1B0C6]">{c.count} visit{c.count > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unpaid attempts */}
      <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/25 rounded-sm p-6" data-testid="ipo-unpaid-attempts">
        <div className="flex items-center gap-2 mb-4">
          <XCircle className="w-4 h-4 text-[#F87171]" />
          <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS NOT PROCESSED (LAST {(s.unpaid_attempts || []).length})</h2>
        </div>
        {(s.unpaid_attempts || []).length === 0 ? (
          <div className="text-sm text-[#A1B0C6]">No unpaid checkout attempts.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A1B0C6] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2">REASON</th>
              </tr>
            </thead>
            <tbody>
              {(s.unpaid_attempts || []).map((a, i) => (
                <tr key={i} className="border-b border-white/5 align-top">
                  <td className="py-2.5 pr-3 text-[#A1B0C6] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{a.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{a.country || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      a.stripe_status === "expired" || a.status === "expired"
                        ? "bg-[#F87171]/15 text-[#FCA5A5]"
                        : "bg-[#FBBF24]/15 text-[#FDE68A]"
                    }`}>{a.stripe_status || a.status || "created"}</span>
                  </td>
                  <td className="py-2.5 text-[#A1B0C6] text-[12px]">{a.reason || "Not checked yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent paid orders */}
      <div className="bg-[#0B1D3A]/70 border border-[#3F8EFC]/25 rounded-sm p-6" data-testid="ipo-recent-orders">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-[#3F8EFC]" />
          <h2 className="text-sm font-bold tracking-wider text-white">RECENT PAID ORDERS ({(s.recent_orders || []).length})</h2>
        </div>
        {(s.recent_orders || []).length === 0 ? (
          <div className="text-sm text-[#A1B0C6]">No paid orders yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A1B0C6] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2 pr-3">AMOUNT</th>
                <th className="py-2">DOWNLOADS</th>
              </tr>
            </thead>
            <tbody>
              {(s.recent_orders || []).map((o, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2.5 pr-3 text-[#A1B0C6] whitespace-nowrap">{fmtDate(o.paid_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{o.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{o.country || "—"}</td>
                  <td className="py-2.5 pr-3 text-[#3F8EFC] font-bold">${((o.amount_paid || 0) / 100).toFixed(2)}</td>
                  <td className="py-2.5 text-white font-bold">{o.downloads || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-[#A1B0C6] flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" />
        IPO Access Dossier™ · replyrescue.app/ipoaccess · $79 USD · Stripe Checkout (LIVE)
      </div>
    </div>
  );
};
